<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <v-icon left>mdi-clipboard-text-clock</v-icon>
        Audit Logs
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="filter.entityType"
              :items="entityTypes"
              item-text="text"
              item-value="value"
              label="Entity Type"
              clearable
              dense
              outlined
              @change="fetchLogs"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filter.action"
              :items="actionTypes"
              label="Action"
              clearable
              dense
              outlined
              @change="fetchLogs"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="filter.userId"
              :items="users"
              item-text="firstName"
              item-value="id"
              label="User"
              clearable
              dense
              outlined
              @change="fetchLogs"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="menuStartDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filter.startDate"
                  label="Start Date"
                  append-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="
                    filter.startDate = null;
                    fetchLogs();
                  "
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filter.startDate"
                @input="
                  menuStartDate = false;
                  fetchLogs();
                "
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="menuEndDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filter.endDate"
                  label="End Date"
                  append-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="
                    filter.endDate = null;
                    fetchLogs();
                  "
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filter.endDate"
                @input="
                  menuEndDate = false;
                  fetchLogs();
                "
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="auditLogs"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100],
        }"
        dense
        @click:row="showDetail"
        class="elevation-0"
      >
        <template v-slot:item.action="{ item }">
          <v-chip :color="getActionColor(item.action)" small dark>
            {{ item.actionText }}
          </v-chip>
        </template>

        <template v-slot:item.entityTypeDisplay="{ item }">
          {{ item.entityTypeDisplay }}
        </template>

        <template v-slot:item.changeSummary="{ item }">
          <span class="text-truncate" style="max-width: 300px; display: inline-block;">
            {{ item.changeSummary || "-" }}
          </span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="800" persistent>
      <v-card v-if="selectedLog">
        <v-card-title class="headline primary white--text">
          <v-chip :color="getActionColor(selectedLog.action)" small dark class="mr-2">
            {{ selectedLog.actionText }}
          </v-chip>
          {{ selectedLog.entityTypeDisplay }} #{{ selectedLog.entityId }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="6">
              <div class="caption grey--text">User</div>
              <div>{{ selectedLog.userName }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Date & Time</div>
              <div>{{ formatDateTime(selectedLog.createdAt) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Revision</div>
              <div>#{{ selectedLog.revision }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Entity ID</div>
              <div>{{ selectedLog.entityId }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div
            v-if="selectedLog.changedFields && Object.keys(selectedLog.changedFields).length > 0"
          >
            <div class="subtitle-1 font-weight-medium mb-3">Changes</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Field</th>
                    <th class="text-left">Old Value</th>
                    <th class="text-left">New Value</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(change, fieldName) in selectedLog.changedFields">
                    <tr v-if="isSimpleChange(change)" :key="fieldName">
                      <td class="font-weight-medium">{{ fieldName }}</td>
                      <td>
                        <span class="red--text text--lighten-1">
                          {{ formatValue(change.oldValue) || "(empty)" }}
                        </span>
                      </td>
                      <td>
                        <span class="green--text text--darken-2">
                          {{ formatValue(change.newValue) || "(empty)" }}
                        </span>
                      </td>
                    </tr>
                    <tr v-else :key="fieldName + '-collection'">
                      <td class="font-weight-medium">{{ fieldName }}</td>
                      <td colspan="2">
                        <v-chip
                          v-if="change.added && change.added.length"
                          x-small
                          color="success"
                          class="mr-1"
                        >
                          +{{ change.added.length }} added
                        </v-chip>
                        <v-chip
                          v-if="change.removed && change.removed.length"
                          x-small
                          color="error"
                          class="mr-1"
                        >
                          -{{ change.removed.length }} removed
                        </v-chip>
                        <v-chip
                          v-if="change.modified && change.modified.length"
                          x-small
                          color="warning"
                          class="mr-1"
                        >
                          {{ change.modified.length }} modified
                        </v-chip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <div v-else class="text-center py-4 grey--text">
            {{ selectedLog.action === "CREATE" ? "Record was created" : "Record was deleted" }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "AuditLogs",
  data() {
    return {
      loading: false,
      menuStartDate: false,
      menuEndDate: false,
      detailDialog: false,
      selectedLog: null,
      filter: {
        entityType: null,
        action: null,
        userId: null,
        startDate: null,
        endDate: null,
      },
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
      headers: [
        { text: "Date", value: "createdAt", width: "180px" },
        { text: "Action", value: "action", width: "100px" },
        { text: "Entity", value: "entityTypeDisplay", width: "150px" },
        { text: "Entity ID", value: "entityId", width: "100px" },
        { text: "User", value: "userName", width: "150px" },
        { text: "Changes", value: "changeSummary", sortable: false },
      ],
      actionTypes: [
        { text: "Created", value: "CREATE" },
        { text: "Updated", value: "UPDATE" },
        { text: "Deleted", value: "DELETE" },
      ],
    };
  },
  computed: {
    ...mapState("audit", ["auditLogs", "totalItems", "entityTypes"]),
    ...mapState("user", ["users"]),
  },
  watch: {
    options: {
      handler() {
        this.fetchLogs();
      },
      deep: true,
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      await this.$store.dispatch("user/getAll");
      await this.$store.dispatch("audit/getEntityTypes");
      await this.fetchLogs();
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page - 1,
          size: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0] || "createdAt",
          sortDir: this.options.sortDesc[0] ? "desc" : "asc",
          ...this.filter,
        };

        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === "") {
            delete params[key];
          }
        });

        await this.$store.dispatch("audit/getAll", params);
      } catch (error) {
        this.$store.dispatch("toast", {
          type: "error",
          message: "Failed to load audit logs",
        });
      } finally {
        this.loading = false;
      }
    },
    showDetail(item) {
      this.selectedLog = item;
      this.detailDialog = true;
    },
    getActionColor(action) {
      const colors = {
        CREATE: "success",
        UPDATE: "warning",
        DELETE: "error",
      };
      return colors[action] || "grey";
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      return date.toLocaleString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatValue(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    isSimpleChange(change) {
      return change && (change.oldValue !== undefined || change.newValue !== undefined);
    },
  },
};
</script>

<style scoped>
.v-data-table tbody tr {
  cursor: pointer;
}
.v-data-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
